import { createEntityAdapter, createSlice } from '@reduxjs/toolkit';

import Schedule from 'features/scheduleBuilder/ScheduleBuilderView/resources/Schedule';

import { captureHydrationException } from '../helpers';
import { ScheduleFetchAction } from '../types';

import { SLICE_NAME } from './constants';
import {
  ShiftSerializedEntity,
  ShiftsState,
  ShiftWithRelationships,
} from './types';

export const shiftsAdapter = createEntityAdapter<ShiftWithRelationships>();

const initialState: ShiftsState = {
  ...shiftsAdapter.getInitialState(),
  status: 'idle',
  error: undefined,
};

export const slice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    setAll: shiftsAdapter.setAll,
    removeAll: shiftsAdapter.removeAll,
    updateShiftOwner: (state, action) => {
      const { shiftId, jobId } = action.payload;
      const shift = state.entities[shiftId];
      if (shift) {
        shift.owner = { data: { id: jobId, type: 'job' } };
        shift.isOpen = false;
      }
    },
  },
  extraReducers: builder => {
    builder.addCase(
      Schedule.resourceActionEvent('fetch'),
      (state: any, action: ScheduleFetchAction) => {
        try {
          state.status = 'succeeded';
          if (action.payload?.included) {
            try {
              const shifts: ShiftWithRelationships[] = action.payload.included
                .filter(
                  (entity: ShiftSerializedEntity) => entity.type === 'shift'
                )
                .map((entity: ShiftSerializedEntity) => ({
                  id: entity.id,
                  type: entity.type,
                  startAt: entity.attributes.startAt,
                  endAt: entity.attributes.endAt,
                  color: entity.attributes.color,
                  roleName: entity.attributes.roleName,
                  isOpen: entity.attributes.isOpen,
                  isPublished: entity.attributes.isPublished,
                  hasTimecard: entity.attributes.hasTimecard,
                  note: entity.attributes.note,
                  noteAttribute: entity.attributes.note,
                  affectsUnpublishedCounter:
                    entity.attributes.affectsUnpublishedCounter,
                  synced: entity.attributes.synced,
                  syncError: entity.attributes.syncError,
                  isApproved: entity.attributes.isApproved,
                  approvalEvent: entity.attributes.approvalEvent,
                  role: { data: { id: '', type: 'role' } },
                  owner: { data: { id: '', type: 'user' } },
                  shiftEvents: { data: [] },
                  conflict: { data: [] },
                  ...Object.fromEntries(
                    Object.entries(entity.relationships || {}).map(
                      ([k, v]: [string, any]) => [k, v.data]
                    )
                  ),
                }));
              shiftsAdapter.setAll(state, shifts);
            } catch (error) {
              captureHydrationException(state, error, SLICE_NAME);
            }
          }
        } catch (error) {
          captureHydrationException(state, error, SLICE_NAME);
        }
      }
    );
  },
});

export const { setAll, removeAll, updateShiftOwner } = slice.actions;
export const { reducer } = slice;
